import moment from 'moment/min/moment-with-locales';
import { getDay, startOfYear, endOfYear, subYears } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

const dayIndexToKey = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ko_format = 'YYYY-MM-DD';
const ko_format_dot = 'YYYY.MM.DD';
const ko_format_full = 'YYYY-MM-DD HH:mm';
const ko_format_dateTime = 'YYYY.MM.DD HH:mm:ss';
const ko_format_half_full = 'YY-MM-DD HH:mm';
const ko_format_small_dot = 'YY.MM.DD';
const ko_format_hm = 'YYYY.MM.DD HH:mm';

const other_format = 'DD/MM/YYYY';
const other_format_full = 'DD/MM/YYYY HH:mm';
const other_format_dateTime = 'DD/MM/YYYY HH:mm:ss';
const other_format_half_full = 'DD/MM/YY HH:mm';
const other_format_small_dot = 'DD/MM/YY';
const other_format_hm = 'DD/MM/YYYY HH:mm';

const hour_format = 'HH:mm';

const ko_format_full_date = 'YYYY년 MM월 DD일 dddd';
const other_format_full_date = 'DD/MM/YYYY dddd';

const ko_format_date = 'YYYY년 MM월 DD일';

const dayName = {
  ko: {
    Mon: '월',
    Tue: '화',
    Wed: '수',
    Thu: '목',
    Fri: '금',
    Sat: '토',
    Sun: '일',
  },
  vi: {
    Mon: 'T2',
    Tue: 'T3',
    Wed: 'T4',
    Thu: 'T5',
    Fri: 'T6',
    Sat: 'T7',
    Sun: 'CN',
  },
};

export function dateFormat(date, locale = 'ko') {
  if (!date) {
    return '';
  }
  let format = other_format;
  if (locale === 'ko') {
    format = ko_format;
  }
  return moment(date).format(format);
}

export function dateFormatDot(date, locale = 'ko') {
  if (!date) return '';
  let format = other_format;
  if (locale === 'ko') {
    format = ko_format_dot;
  }
  return moment(date).format(format);
}

export function dateFormatDotRange(range, locale = 'ko') {
  if (!range) return '';
  return `${dateFormatDot(range.from, locale)} - ${dateFormatDot(range.to, locale)}`;
}

export function smallDotDateFormat(date, locale = 'ko') {
  if (!date) return '';
  let format = other_format_small_dot;
  if (locale === 'ko') {
    format = ko_format_small_dot;
  }
  return moment(date).format(format);
}

export function dotDateFormat(date, locale = 'ko') {
  if (locale === 'vi') {
    return dateFormat(date, locale).replace(/-/g, '/');
  }
  return dateFormat(date, locale).replace(/-/g, '.');
}

export function hmDateFormat(date, locale = 'ko') {
  if (!date) return '';
  let format = other_format_hm;
  if (locale === 'ko') {
    format = ko_format_hm;
  }
  return moment(date).format(format);
}

export function dateHourFormat(date, locale = 'ko') {
  if (!date) {
    return '';
  }
  let format = other_format_full;
  if (locale === 'ko') {
    format = ko_format_full;
  }
  return moment(date).format(format);
}

// todo: 삭제예정
export function stringToDate(date) {
  if (!date) return '';
  return moment(date).format();
}

export function dateTimeFormat(date, locale = 'ko') {
  if (!date) {
    return '';
  }
  let format = other_format_dateTime;
  if (locale === 'ko') {
    format = ko_format_dateTime;
  }
  return moment(date).format(format);
}

export function convertToUtc(date, format = ko_format_full) {
  if (!date) {
    return '';
  }
  return moment(date, format).utc().format();
}

export function startDay(date) {
  return moment(date).startOf('day').format();
}

export function endDay(date) {
  return moment(date).endOf('day').format();
}

export function isBeforeNow(date) {
  return moment().isAfter(moment(date));
}

export function timeFormat(date, locale = 'ko') {
  if (locale === 'ko') {
    if (moment(date).format('A') === 'AM') {
      return `오전 ${moment(date).format('hh:mm')}`;
    } else if (moment(date).format('A') === 'PM') {
      return `오후 ${moment(date).format('hh:mm')}`;
    } else {
      return moment(date).format('A hh:mm');
    }
  } else {
    if (moment(date).format('A') === 'SA') {
      return `${moment(date).format('hh:mm')} AM`;
    } else if (moment(date).format('A') === 'CH') {
      return `${moment(date).format('hh:mm')} PM`;
    } else {
      return moment(date).format('A hh:mm');
    }
  }
}

export function hourFormat(date) {
  if (!date) {
    return '';
  }
  return moment(date).format(hour_format);
}

export function todayDateFormat(locale = 'ko') {
  let format = other_format;
  if (locale === 'ko') {
    format = ko_format;
  }
  return moment().format(format);
}

function getDayName(date, locale = 'ko') {
  if (!date) return null;
  const name = moment(date).locale('en').format('ddd');
  return dayName[locale][name];
}

export function returnDifferenceFromNow(date, locale = 'ko') {
  const today = moment().startOf('day');
  const diffDay = moment(date).startOf('day');
  if (diffDay.isBefore(today)) return '';
  const diff = diffDay.diff(today, 'days');
  return locale === 'ko' ? ` (${diff}일 남음)` : ` (${diff}days remains)`;
}

export function dotOnlyDateWithDayName(date, locale = 'ko') {
  if (!date) return '';
  if (locale === 'vi') {
    return `${getDayName(date, locale) || ''} ${dateFormat(date, locale).replace(/-/g, '/')}`;
  }
  return `${dateFormat(date, locale).replace(/-/g, '.')}(${getDayName(date, locale) || ''})`;
}

export function fullDateFormat(date, locale = 'ko') {
  if (!date) {
    return '';
  }
  let customMoment = moment(date);
  let format = other_format_full_date;
  if (locale === 'ko') {
    customMoment = customMoment.locale('ko');
    format = ko_format_full_date;
  }
  return customMoment.format(format);
}

export function koDateFormat(date, locale = 'ko') {
  if (!date) return '';
  let format = other_format;
  if (locale === 'ko') {
    format = ko_format_date;
  }
  return moment(date).format(format);
}

export function koDateTimeFormat(date, locale = 'ko') {
  if (!date) return '';
  if (locale !== 'ko') {
    return moment(date).format(other_format_full);
  }
  return `${koDateFormat(date, locale)} ${timeFormat(date, locale)}`;
}

export function checkDatePassedOneDay(date) {
  if (!date) return false;
  const now = moment();
  const target = moment(date).add(1, 'days');
  return now.isAfter(target);
}

export function dateHalfFullFormat(date, locale = 'ko') {
  if (!date) return '';
  let format = other_format_half_full;
  if (locale === 'ko') {
    format = ko_format_half_full;
  }
  return moment(date).format(format);
}

export function getStandardDate(locale = 'ko') {
  const today = moment();
  const offset = locale === 'ko' ? 9 : 7;
  const yearStart = today.clone().startOf('year').utcOffset(offset);
  const yearEnd = today.clone().endOf('year').utcOffset(offset);
  const lastYearStart = today.clone().subtract(1, 'years').startOf('year').utcOffset(offset);
  const lastYearEnd = today.clone().subtract(1, 'years').endOf('year').utcOffset(offset);
  return { year: [yearStart, yearEnd], lastYear: [lastYearStart, lastYearEnd] };
}

export function getDotFormat(locale = 'ko') {
  return locale === 'ko' ? 'YYYY.MM.DD' : 'DD/MM/YYYY';
}

export function getLetterFormat(locale = 'ko') {
  return locale === 'ko' ? 'YYYY년 MM월 DD일' : 'DD/MM/YYYY';
}

export function getSlashFormat(locale = 'ko') {
  return locale === 'ko' ? 'YYYY/MM/DD' : 'DD/MM/YYYY';
}

export function getDateMoment(date) {
  if (!date) return moment();
  return moment(date);
}

export function getDateWithDayNameFormat(date, locale = 'ko') {
  if (!date) return getDotFormat(locale);
  if (locale === 'vi') {
    return `[${getDayName(date, locale) || ''}] DD/MM/YYYY`;
  }
  return `YYYY.MM.DD(${getDayName(date, locale) || ''})`;
}

export function getDateWithDayNameFormatMoment(date, locale = 'ko') {
  let format = '';
  if (!date) {
    format = getDotFormat(locale);
  }
  if (locale === 'vi') {
    format = `[${getDayName(date, locale) || ''}] DD/MM/YYYY`;
  } else {
    format = `YYYY.MM.DD(${getDayName(date, locale) || ''})`;
  }
  return moment(date).format(format);
}

export function getDateWithDayNameHalfFormatMoment(date, locale = 'ko') {
  let format = '';
  if (!date) {
    format = getDotFormat(locale);
  }
  if (locale === 'vi') {
    format = `[${getDayName(date, locale) || ''}] DD/MM/YY`;
  } else {
    format = `YY-MM-DD(${getDayName(date, locale) || ''})`;
  }
  return moment(date).format(format);
}

export function getDateWithDayNameKoFormatMoment(date, locale = 'ko') {
  let format = '';
  if (!date) {
    format = getDotFormat(locale);
  }
  if (locale === 'vi') {
    format = `[${getDayName(date, locale) || ''}] DD/MM/YYYY`;
  } else {
    format = `YYYY년 MM월 DD일(${getDayName(date, locale) || ''})`;
  }
  return moment(date).format(format);
}

export function getDateTimeWithDayNameKoFormatMoment(date, locale = 'ko') {
  let format = '';
  if (!date) {
    format = getDotFormat(locale);
  }
  if (locale === 'vi') {
    format = `[${getDayName(date, locale) || ''}] DD/MM/YYYY HH:mm`;
  } else {
    format = `YYYY년 MM월 DD일(${getDayName(date, locale) || ''}) HH:mm`;
  }
  return moment(date).format(format);
}

export function getDateWithDayNameDashFormatMoment(date, locale = 'ko') {
  let format = '';
  if (!date) {
    format = getDotFormat(locale);
  }
  if (locale === 'vi') {
    format = `[${getDayName(date, locale) || ''}] DD/MM/YYYY`;
  } else {
    format = `YYYY-MM-DD (${getDayName(date, locale) || ''})`;
  }
  return moment(date).format(format);
}

export function getCalendarInitDateRange() {
  return [moment().startOf('month').subtract(7, 'day').format(), moment().endOf('month').add(7, 'day').format()];
}

export function getCalendarInitDate() {
  return moment().toDate();
}

export function isSameDay(date1, date2) {
  return moment(date1).isSame(moment(date2), 'day');
}

export function isBeforeDate(date1, date2) {
  return moment(date1).isBefore(moment(date2));
}

export function getStringNowDatePlus1Hour() {
  return moment().add(1, 'hours').hour().toString();
}

export function getNowDate() {
  return moment().format();
}

export function diffSecondsFromNow(date) {
  if (!date) return 0;
  return moment(date).diff(moment(), 'seconds');
}

export function getDateStartOfMonth(subtract = 0) {
  return moment().subtract(subtract, 'months').startOf('month');
}

export function getDateEndOfMonth(subtract = 0) {
  return moment().subtract(subtract, 'months').endOf('month');
}

export function getDateUtcFormatStartOfMonth() {
  return moment().startOf('month').toISOString();
}

export function getDateUtcFormatEndOfMonth() {
  return moment().endOf('month').toISOString();
}

// 서버로 보내는 UTC 기준 0시
export function convertISOformatOnlyDateUTC(date) {
  if (!date) return '';
  return `${moment(date).format('YYYY-MM-DD')}T00:00:00.000Z`;
}

// 서버로 보내는 로컬기준 0시(시작일)
export function convertISOformatOnlyDate(date) {
  if (!date) return '';
  return moment(date).startOf('day').toISOString();
}

// 서버로 보내는 로컬기준 11:59:59(종료일)
export function convertISOformatOnlyDateEnd(date) {
  if (!date) return '';
  return moment(date).endOf('day').toISOString();
}

// 서버로 보내는 시간포함 날짜
export function convertISOFormat(date) {
  if (!date) return '';
  return moment(date).toISOString();
}

// 서버로 보내는 일자 + 시간
export function getTotalDate(date, time) {
  if (!date || !time) return '';
  return moment(`${moment(date).format('YYYY-MM-DD')}T${time}:00`).toISOString();
}

export function getYear(date) {
  if (!date) return '';
  return moment(date).format('YYYY');
}

// dayPicker(date-fns)
export function formatDate(date) {
  if (!date) return '';
  return date
    .toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\. /g, '-')
    .replace(/\./g, '');
}

export function formatDateOnlyNum(date) {
  if (!date) return '';
  return date
    ?.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\. /g, '')
    .replace(/\./g, '');
}

export function parseDateObject(str, isEnd = false) {
  if (!str || str.length !== 8) return undefined;
  const year = parseInt(str.slice(0, 4), 10);
  const month = parseInt(str.slice(4, 6), 10) - 1;
  const day = parseInt(str.slice(6, 8), 10);
  const date = new Date(year, month, day);
  if (isEnd) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date;
}

// 오늘, 1주, 1개월, 3개월
// TODAY, 1WEEK, 1MONTH, 3MONTH
export function getDateRange(range) {
  const today = new Date();
  const to = new Date(today);
  to.setHours(23, 59, 59, 999);

  const from = new Date(today);
  if (range === 'TODAY') {
    from.setHours(0, 0, 0, 0);
  } else if (range === '1WEEK') {
    from.setDate(from.getDate() - 7);
    from.setHours(0, 0, 0, 0);
  } else if (range === '1MONTH') {
    from.setMonth(from.getMonth() - 1);
    from.setHours(0, 0, 0, 0);
  } else if (range === '3MONTH') {
    from.setMonth(from.getMonth() - 3);
    from.setHours(0, 0, 0, 0);
  } else {
    return { from: undefined, to: undefined };
  }

  return { from, to };
}

function normalizeToMidnight(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isDateWithinDayRange(target, start, end) {
  if (!target || !start || !end) return false;
  const t = normalizeToMidnight(target).getTime();
  const s = normalizeToMidnight(start).getTime();
  const e = normalizeToMidnight(end).getTime();

  return s <= t && t <= e;
}

export function getDotFormatDateFns(locale = 'ko') {
  return locale === 'ko' ? 'yyyy.MM.dd' : 'dd/MM/yyyy';
}

export function getDateWithDayNameFormatDateFns(date, locale = 'ko') {
  if (!date) return getDotFormatDateFns(locale);
  if (locale === 'vi') {
    return `['${getDayNameDateFns(date, locale) || ''}'] dd/MM/yyyy`;
  }
  return `yyyy.MM.dd(${getDayNameDateFns(date, locale) || ''})`;
}

function getDayNameDateFns(date, locale = 'ko') {
  if (!date) return null;
  const dayIndex = getDay(new Date(date));
  const dayKey = dayIndexToKey[dayIndex];
  return dayName[locale]?.[dayKey] || dayKey;
}
export function getStandardDateDateFns(locale = 'ko') {
  const timeZone = locale === 'ko' ? 'Asia/Seoul' : 'Asia/Ho_Chi_Minh';

  const now = new Date();

  const yearStart = zonedTimeToUtc(startOfYear(now), timeZone);
  const yearEnd = zonedTimeToUtc(endOfYear(now), timeZone);

  const lastYearStart = zonedTimeToUtc(startOfYear(subYears(now, 1)), timeZone);
  const lastYearEnd = zonedTimeToUtc(endOfYear(subYears(now, 1)), timeZone);

  return {
    year: [yearStart, yearEnd],
    lastYear: [lastYearStart, lastYearEnd],
  };
}

export function getLetterFormatDateFns(locale = 'ko') {
  return locale === 'ko' ? 'yyyy년 MM월 dd일' : 'dd/MM/yyyy';
}

export function getSlashFormatDateFns(locale = 'ko') {
  return locale === 'ko' ? 'yyyy/MM/dd' : 'dd/MM/yyyy';
}

export function getDashFormatDateFns(locale = 'ko') {
  return locale === 'ko' ? 'yyyy-MM-dd' : 'dd-MM-yyyy';
}

export function koCalculatePeriod(start, end, locale = 'ko') {
  if (locale !== 'ko') {
    if (!start || !end) return '';
    const format = other_format;
    return `${moment(start).format(format)} ~ ${moment(end).format(format)}`;
  }
  if (!start) return '';
  const format = ko_format_date;
  const startText = moment(start).format(format);
  const endText = end ? moment(end).format(format) : '현재';
  const diff = end ? moment(end).diff(moment(start), 'months') : moment().diff(moment(start), 'months');
  const years = Math.floor(diff / 12);
  const months = diff % 12;
  let diffText = '';
  if (years > 0 && months > 0) {
    diffText = `${years}년 ${months}개월`;
  } else if (years > 0) {
    diffText = `${years}년`;
  } else if (months > 0) {
    diffText = `${months}개월`;
  } else {
    diffText = '1개월 이내';
  }
  return `${startText} - ${endText} (${diffText})`;
}
